// src/services/sharePointService.js
import { sharePointConfig } from "../authConfig";

const { siteUrl, listName, fields } = sharePointConfig;

function buildEndpoint({ filters = [], search = "", top = 500 } = {}) {
  const base = `${siteUrl}/_api/web/lists/getbytitle('${listName}')/items`;
  // Always include required fields regardless of .env config
  const requiredFields = ["IsActive", "IsNew", "DiscoveryQuestions", "Modified"];
  const select = [...new Set([...Object.values(fields), ...requiredFields])].filter(Boolean).join(",");

  const filterParts = [];
  filters.forEach(({ field, value }) => {
    filterParts.push(`${field} eq '${value}'`);
  });
  if (search) {
    filterParts.push(
      `(substringof('${search}', ${fields.programName}) or substringof('${search}', ${fields.productCode}))`
    );
  }

  const params = new URLSearchParams({
    $select: select,
    $top: String(top),
    $orderby: `${fields.productCode} asc`,
  });
  if (filterParts.length) params.set("$filter", filterParts.join(" and "));

  return `${base}?${params.toString()}`;
}

export async function fetchCourses(accessToken, queryOptions = {}) {
  const { practices = [], solutionTypes = [], search = "", salesReady = [] } = queryOptions;

  const filters = [];
  if (practices.length === 1) filters.push({ field: fields.practice, value: practices[0] });
  if (solutionTypes.length === 1) filters.push({ field: fields.solutionType, value: solutionTypes[0] });

  const endpoint = buildEndpoint({ filters, search });

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json;odata=nometadata",
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SharePoint API error ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  let items = (data.value || []).map(mapItem);

  // Client-side filters for multiple values
  if (practices.length > 1)     items = items.filter((i) => practices.includes(i.practice));
  if (solutionTypes.length > 1) items = items.filter((i) => solutionTypes.includes(i.solutionType));

  // Sales Ready filters
  if (salesReady.includes("coverEmail"))         items = items.filter((i) => i.coverEmail);
  if (salesReady.includes("productNote"))        items = items.filter((i) => i.approachNote);
  if (salesReady.includes("successStory"))       items = items.filter((i) => i.successStory);
  if (salesReady.includes("discoveryQuestions")) items = items.filter((i) => i.discoveryQuestions);

  return items;
}

function mapItem(raw) {
  return {
    id:                  raw.ID || raw.Id,
    productCode:         raw[fields.productCode]  || "",
    programName:         raw[fields.programName]  || "",
    practice:            raw[fields.practice]     || "",
    solutionType:        raw[fields.solutionType] || "",
    coverEmail:          raw[fields.coverEmail]   || null,
    approachNote:        raw[fields.approachNote] || null,
    successStory:        raw[fields.successStory] || null,
    discoveryQuestions:  raw["DiscoveryQuestions"] || null,
    priority:            !!raw[fields.priority],
    isNew:               !!raw["IsNew"],
    isActive:            raw["IsActive"] !== "No",
    modified:            raw["Modified"] || null,
    expiry:              raw[fields.expiry]   || null,
  };
}
